<script setup lang="ts">
//#region imports
import { computed, nextTick, ref } from 'vue';
import { useTypedStore } from '@/store';
import { useLodash } from '@/setup/lodash';
import { useApi } from '@/api';
import type { EmitsToListeners } from '@/types/core';
import type { GetDomainsManagementPlatformServiceDomainsByIdResponse } from '@/types/openapi-ts';
//#endregion

//#region types
interface Props {
  domainId: string;
  canEditServiceDomain: boolean;
}

interface  Emits {
  'update': [],
}

export interface BlockedCountriesParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region const
const fields = [
  {
    name: 'country_name',
  },
];
//#endregion

//#region composables
const api = useApi();
const _ = useLodash();
const store = useTypedStore();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);

const countryCode = ref('');
const tableData = ref<GetDomainsManagementPlatformServiceDomainsByIdResponse['payload']['blocked_countries']>([]);
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);

const filteredCountries = computed(() => {
  return _.differenceWith(countries.value, tableData.value!, (country, tableItem) => {
    return country.code === tableItem.country_code;
  });
});
//#endregion

//#region methods
async function open() {
  await fetchData();

  await nextTick();
  isVisible.value = true;
}

function handleClose() {
  isVisible.value = false;
}

async function addCountry() {
  isLoading.value = true;
  try {
    await api.updateBlockedCountry(props.domainId, { country_code: countryCode.value });
    await fetchData();
    countryCode.value = '';
    emit('update');
  } finally {
    isLoading.value = false;
  }
}

async function deleteCountry({ country_code }: { country_code: string }) {
  isLoading.value = true;
  try {
    await api.deleteBlockedCountry(props.domainId, { country_code });
    await fetchData();
    emit('update');
  } finally {
    isLoading.value = false;
  }
}

async function fetchData() {
  isLoading.value = true;
  try {
    const { data: { payload } } = await api.getServiceDomainById(props.domainId);
    tableData.value = payload.blocked_countries ?? [];
  } finally {
    isLoading.value = false;
  }
}
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="blocked-countries-popup"
    :title="$t('domainManagement.blockedPopup.title')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <div
          v-if="canEditServiceDomain"
          class="t:flex t:items-end t:space-x-8"
        >
          <UiSelectElement
            v-model="countryCode"
            :placeholder="$t('domainManagement.blockedPopup.addCountryPlaceholder')"
            :label="$t('domainManagement.blockedPopup.addCountry')"
            :list="filteredCountries"
            option-value-param="code"
            filterable
            size="big"
          />
          <UiButton
            :disabled="!countryCode"
            icon="plus"
            size="big"
            @click="addCountry"
          >
            {{ $t('ui.buttons.add') }}
          </UiButton>
        </div>
        <UiTableNew
          v-loading="isLoading"
          :fields="fields"
          :data="tableData"
          disable-sort
          :padding="0"
          is-static
          :max-table-height="240"
          i18n-path="domainManagement.blockedPopup"
        >
          <template
            v-if="canEditServiceDomain"
            v-slot:append
          >
            <ElTableColumn
              align="center"
              width="100"
            >
              <template v-slot:header="scope">
                <div :key="scope.$index">
                  {{ $t('settings.tokens.actions') }}
                </div>
              </template>
              <template v-slot="scope">
                <ActionIcon
                  icon="trash-alt"
                  color="var(--text-negative)"
                  :tooltip="$t('domainManagement.buttons.delete')"
                  @click="deleteCountry(scope.row)"
                />
              </template>
            </ElTableColumn>
          </template>
        </UiTableNew>
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex">
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleClose"
        >
          {{ $t('crm.buttons.close') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>
